import { useEffect, useState, useRef } from 'react';

/**
 * Custom pull-to-refresh hook for mobile PWA feel.
 *  - Triggers `onRefresh()` when the user drags the top of the page down ≥ 70px
 *  - Returns indicator state so the UI can render a spinner / progress bar
 *  - Only active when the page is scrolled to the very top (window.scrollY = 0)
 *  - Stays out of the way on desktop and within scrollable sub-containers
 */
export const usePullToRefresh = (onRefresh, { threshold = 70, enabled = true } = {}) => {
  const [pull, setPull] = useState(0);          // 0..1 progress
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const activeRef = useRef(false);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const onTouchStart = (e) => {
      if (window.scrollY > 4) return;
      if (refreshing) return;
      startY.current = e.touches[0].clientY;
      activeRef.current = true;
    };

    const onTouchMove = (e) => {
      if (!activeRef.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy <= 0) {
        if (pull !== 0) setPull(0);
        return;
      }
      // Resistance curve so it feels like real pull-to-refresh
      const damped = Math.min(dy * 0.55, threshold * 1.3);
      setPull(damped / threshold);
    };

    const onTouchEnd = async () => {
      if (!activeRef.current) return;
      activeRef.current = false;
      if (pull >= 1 && !refreshing) {
        setRefreshing(true);
        setPull(1);
        try { await onRefresh?.(); } catch { /* swallow */ }
        // small minimum so the spinner is perceivable
        setTimeout(() => {
          setRefreshing(false);
          setPull(0);
        }, 500);
      } else {
        setPull(0);
      }
      startY.current = null;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [onRefresh, threshold, enabled, pull, refreshing]);

  return { pull, refreshing };
};
